import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PushQuoteService } from '@zero/shared/ui-push';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { tryGetQuoteIsin } from '@zero/shared/util-helper';
import { TopInstrument, TopInstrumentQuote } from '@zero/shared/util-models';
import { FamousIndication } from 'constants/shared-constants';
import { isEmpty } from 'lodash-es';
import { catchError, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Indication } from '../entities/indication.model';
import { HomeInfrastructure } from '../infrastructure/home.infrastructure';
import { homeActions, homeApiActions } from './home.actions';
import { homeFeature } from './home.reducers';

@Injectable({ providedIn: 'root' })
export class HomeEffects {
  readonly #actions = inject(Actions);
  readonly #store = inject(Store);
  readonly #homeInfrastructure = inject(HomeInfrastructure);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public initializeFamousIndications$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(homeActions.setFamousIndications),
      map(({ indications }) => ({
        indications,
        isins: indications.map(({ isin }) => tryGetQuoteIsin(isin)),
      })),
      switchMap(({ indications, isins }) =>
        forkJoin({
          quotes: this.#homeInfrastructure.getQuotes(isins),
          prevQuotes: this.#homeInfrastructure.getPrevQuotes(isins),
        }).pipe(
          map(({ quotes, prevQuotes }) =>
            indications.map((entry: FamousIndication) => {
              const quoteIsin = tryGetQuoteIsin(entry.isin);
              const quote = quotes[quoteIsin];
              const prevQuoteList = prevQuotes[quoteIsin];
              const last = quote?.b ?? 0;

              const prev = {
                previous: last,
                previous2: 0,
                previousTS: prevQuoteList?.[0]?.ts ? Date.parse(prevQuoteList[0].ts) : null,
                previous2TS: prevQuoteList?.[1]?.ts ? Date.parse(prevQuoteList[1].ts) : null,
              };

              if (prevQuoteList?.length > 0) {
                prev.previous = prevQuoteList[0].bid ?? last;
                if (prevQuoteList[0].ts && quote?.t && Date.parse(prevQuoteList[0].ts) === quote.t && prevQuoteList.length > 1) {
                  prev.previous = prevQuoteList[1].bid ?? last;
                }
                prev.previous2 = prevQuoteList?.[1]?.bid ?? null;
              }

              return {
                ...entry,
                last,
                ...prev,
                percent: calcPercent(last, prev.previous),
                time: quote?.t,
                priceScale: entry.priceScale ?? 2,
                specialLeverageProduct: entry.specialLeverageProduct,
              } as Indication;
            }),
          ),
          map((indications) => homeActions.updateFamousIndications({ indications })),
        ),
      ),
    );
  });

  public subscribeToIndicationQuotes$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(homeActions.subscribeToIndicationQuotes),
      switchMap(() =>
        this.#store.select(homeFeature.selectIndicationsQuoteISINs).pipe(
          switchMap((isins) =>
            this.#pushQuoteService.subscribeMultiBuffered(isins).pipe(
              filter((quotes) => !isEmpty(quotes)),
              map((quotes) => homeActions.updateIndicationQuotes({ quotes })),
            ),
          ),
        ),
      ),
    );
  });

  public unsubscribeFromIndicationQuotes$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(homeActions.unsubscribeFromIndicationQuotes),
        concatLatestFrom(() => this.#store.select(homeFeature.selectIndicationsQuoteISINs)),
        tap(([, isins]) => this.#pushQuoteService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public updateIndicationQuotes$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(homeActions.updateIndicationQuotes),
      concatLatestFrom(() => this.#store.select(homeFeature.selectIndications)),
      map(([{ quotes }, indications]) =>
        indications.map((instrument) => {
          const quoteIsin = tryGetQuoteIsin(instrument.isin);
          const quote = quotes[quoteIsin];

          if (!quote?.bid) {
            return instrument;
          }

          let previous = instrument.previous;
          if (instrument.previousTS && quote.time && instrument.previousTS >= Date.parse(quote.time) && instrument.previous2) {
            previous = instrument.previous2;
          }

          return {
            ...instrument,
            last: quote.bid,
            percent: calcPercent(quote.bid, previous),
            absolute: quote.bid - instrument.previous,
            time: quote.time,
          };
        }),
      ),
      map((indications) => homeActions.updateFamousIndications({ indications })),
    );
  });

  public loadTopInstruments$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(homeActions.loadTopInstruments),
      switchMap(() =>
        this.#homeInfrastructure.getTopInstruments().pipe(
          map((topInstruments) => topInstruments.map(calculatePerf)),
          map((topInstruments) => homeApiActions.loadTopInstrumentsSuccessful({ topInstruments })),
          catchError((error) => of(homeApiActions.loadTopInstrumentsFailed({ error }))),
        ),
      ),
    );
  });

  public subscribeToTopInstrumentsQuotes$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(homeActions.subscribeToTopInstrumentsQuotes),
      switchMap(() =>
        this.#store.select(homeFeature.selectMostTradedInstrumentsISINs).pipe(
          switchMap((isins) =>
            this.#pushQuoteService.subscribeMultiBuffered(isins).pipe(
              filter((quotes) => !isEmpty(quotes)),
              map((quotes) => homeActions.updateTopInstrumentsQuotes({ quotes })),
            ),
          ),
        ),
      ),
    );
  });

  public unsubscribeFromTopInstrumentsQuotes$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(homeActions.unsubscribeFromTopInstrumentsQuotes),
        concatLatestFrom(() => this.#store.select(homeFeature.selectMostTradedInstrumentsISINs)),
        tap(([, isins]) => this.#pushQuoteService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public updateTopInstrumentsQuotes$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(homeActions.updateTopInstrumentsQuotes),
      concatLatestFrom(() => this.#store.select(homeFeature.selectMostTradedInstruments)),
      map(([{ quotes }, topInstruments]) => {
        return topInstruments.map((instrument) => {
          const quote = quotes[instrument.isin];

          if (!quote?.bid) {
            return instrument;
          }

          if (quote.time === instrument.time || !quote.bid) {
            return instrument;
          }

          const updatedQuote = instrument.quote || ({} as TopInstrumentQuote);

          const updatedInstrument = {
            ...instrument,
            time: quote.time,
            quote: {
              ...updatedQuote,
              b: quote.bid,
              a: quote.ask,
              value: quote.ask,
            },
            perf: 100,
            perfAbs: 0,
          };

          return calculatePerf(updatedInstrument);
        });
      }),
      map((topInstruments) => homeActions.updateTopInstruments({ topInstruments })),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(homeApiActions.loadTopInstrumentsFailed),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}

const calcPercent = (curr: number, prev: number): number => ((curr - prev) / prev) * 100;

const calculatePerf = (instrument: TopInstrument) => {
  if (!instrument?.quote?.b) {
    instrument.perf = 0;
    return instrument;
  }
  if (!instrument?.historicQuote?.bid) {
    instrument.perf = 0;
    return instrument;
  }
  instrument.perf = (instrument.quote.b / instrument.historicQuote.bid - 1) * 100;
  instrument.perfAbs = instrument.quote.b - instrument.historicQuote.bid;
  return instrument;
};
